import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { TSTitleCard } from '@/components/shared/card/Card';
import { handleMutationResult } from '@/helpers/toastHelper';
import { SkillType } from '@/services/tasks/skillSettings.types';
import { useSkills, useUpdateSkills } from '@/services/tasks/useSkills';
import { strings } from '@/services/translation/strings';
import { FunctionComponent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FiStar } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../../admin.translations';

interface AdminEditSkillDetailsCardProps {
  skill: SkillType;
}

interface EditSkillFormValues {
  name: string;
}

const AdminEditSkillDetailsCard: FunctionComponent<AdminEditSkillDetailsCardProps> = ({ skill }) => {
  const navigate = useNavigate();

  const methods = useForm<EditSkillFormValues>({
    defaultValues: {
      name: skill.name
    }
  });

  useEffect(() => {
    methods.reset({
      name: skill.name
    });
  }, [skill]);

  const { fetching, update } = useUpdateSkills();

  const skills = useSkills();

  const onSubmit = async (values: EditSkillFormValues) => {
    const result = await update([
      {
        ...skill,
        id: skill.id,
        name: values.name
      }
    ]);

    await handleMutationResult(result, {
      successMessage: adminStrings.skillsSection.editSkill.successMessage(values.name),
      errorMessage: adminStrings.skillsSection.editSkill.errorMessage(skill.name),
      onSuccess: () => {
        handleClose();
      }
    });
  };

  const handleClose = () => {
    methods.reset();
    navigate('/admin/skills');
  };

  return (
    <TSTitleCard title={adminStrings.skillsSection.editSkill.title(skill.name)} icon={FiStar} className="w-full">
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <TextField
              label={adminStrings.skillsSection.editSkill.name}
              name="name"
              id="name"
              placeholder={adminStrings.skillsSection.editSkill.skillName}
              customValidatorMessage={methods.formState.errors.name?.message}
              required
              rules={{
                validate: (value: string) => {
                  const isDuplicate = skills?.some(l => l?.name === value && l?.id !== skill.id);
                  if (isDuplicate) return adminStrings.skillsSection.editSkill.uniqueName;
                  return true;
                }
              }}
            />

            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={handleClose}
                loading={fetching}
                submitText={strings.common.save}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </TSTitleCard>
  );
};

export default AdminEditSkillDetailsCard;
